import classNames from "classnames";
import keys from "lodash/keys";
import map from "lodash/map";
import orderBy from "lodash/orderBy";
import { Flex, Inline, Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { Typography } from "PFComponents/typography";
import { useActivities } from "PFCore/hooks/queries/activities/use_activities";
import { Activity } from "PFCore/types/activity";
import { EconomicsScenarioDetails } from "PFCore/types/economics_scenarios";
import { Id } from "PFTypes";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useEconomicsPermissions } from "../show/hooks/use_economics_permissions";
import { RoleCard } from "./role_card";
import { ScenarioBreadcrumbs } from "./scenario_breadcrumbs";
import { ScenarioSummary } from "./scenario_summary";
import { ScenarioToolbox } from "./scenario_toolbox";
import css from "./scenario_view.module.scss";
import { useScenarioFillAndBook } from "./use_scenario_fill_and_book";
import { groupVacanciesByRole } from "./utils";

type ScenarioViewProps = {
  activity: Activity;
  scenario: EconomicsScenarioDetails;
  isFetchingScenario?: boolean;
};

export const ScenarioView = ({ activity, scenario, isFetchingScenario }: ScenarioViewProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "scenario" });

  const { canManageScenario } = useEconomicsPermissions();

  const [selectedVacancies, setSelectedVacancies] = useState<Id[]>([]);

  const isEditable = useMemo(
    () => (scenario ? canManageScenario(scenario.creatorId) : false),
    [scenario, canManageScenario]
  );
  const groupedVacancies = useMemo(
    () => (scenario?.vacancies ? groupVacanciesByRole(scenario.vacancies) : {}),
    [scenario]
  );

  const { closedVacancies, totalVacancies } = useMemo(() => {
    const allVacancies = scenario?.vacancies || [];
    return {
      closedVacancies: allVacancies.filter((vacancy) => !vacancy.opened).length,
      totalVacancies: allVacancies.length
    };
  }, [scenario?.vacancies]);

  const rolesIds = keys(groupedVacancies);
  const { data: activities = [], isLoading: isLoadingActivities } = useActivities<Activity[]>(
    {
      filters: {
        fields: {
          id: rolesIds
        }
      }
    },
    {
      enabled: rolesIds.length > 0,
      select: (data) => data.entries.map(({ activity }) => activity)
    }
  );
  const {
    hasFillAndBookButton,
    isLoadingShortlists,
    fillAndBookCounter,
    handleFill,
    handleFillAndBook,
    checkCanSelectVacancy
  } = useScenarioFillAndBook({
    selectedVacancies,
    setSelectedVacancies,
    scenario,
    activities
  });

  const onSelectVacancy = useCallback((vacancyId: Id, select: boolean) => {
    setSelectedVacancies((selection) =>
      select ? [...selection, vacancyId] : selection.filter((id) => id !== vacancyId)
    );
  }, []);

  const isDataForRolesLoaded = !isLoadingActivities && !isLoadingShortlists;

  return (
    <Flex center className={css.container}>
      <Stack as="main" className={css.root} gap="spacingLg">
        <ScenarioBreadcrumbs
          activityId={activity.id.toString()}
          activityName={activity.name}
          scenarioName={scenario?.name}
        />
        <Inline as="header" spread alignItems="center">
          <Typography variant="h1" textOverflow="clip">
            {scenario?.name}
          </Typography>
          <ScenarioToolbox
            activity={activity}
            scenario={scenario}
            isEditable={isEditable}
            activities={activities}
            selectedVacancies={selectedVacancies}
            hasFillAndBookButton={hasFillAndBookButton}
            fillAndBookCounter={fillAndBookCounter}
            handleFill={handleFill}
            handleFillAndBook={handleFillAndBook}
          />
        </Inline>
        <Inline
          gap="spacingXl"
          alignItems="start"
          className={classNames(css.content, { [css.disabled]: isFetchingScenario })}
        >
          {scenario && <ScenarioSummary activity={activity} scenario={scenario} />}

          {isDataForRolesLoaded ? (
            <Stack as="article" className={css.scenarioDetails}>
              <Typography variant="h5">
                {t("vacanciesAssigned", { closed: closedVacancies, total: totalVacancies })}
              </Typography>
              {map(groupedVacancies, (roleVacancies, roleId) => {
                const roleSelectedVacancies = selectedVacancies.filter(
                  (vacancyId) => roleVacancies.findIndex(({ id }) => id === vacancyId) >= 0
                );

                const orderedRoleVacancies = orderBy(
                  roleVacancies,
                  (vacancy) => vacancy.assignment?.updatedAt
                );

                return (
                  <RoleCard
                    key={roleId}
                    scenarioId={scenario.id}
                    activity={activity}
                    vacancies={orderedRoleVacancies}
                    isEditable={isEditable}
                    selectedVacancies={roleSelectedVacancies}
                    onSelectVacancy={onSelectVacancy}
                    checkCanSelectVacancy={checkCanSelectVacancy}
                  />
                );
              })}
            </Stack>
          ) : (
            <LoadingDots circlesEnabled circleSize="sm" centered className={css.detailsLoading} />
          )}
        </Inline>
      </Stack>
    </Flex>
  );
};
